import React, { useState, useEffect } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    OutlinedInput,
    Box,
    ListItemText,
    Checkbox,
} from '@mui/material';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

function MultiSelectGroupBy({
    label,
    value,
    name,
    onChange,
    options,
    error = null,
    disabled = false,
    variant = 'outlined',
    ...other
}) {
    /* VARIABLE DECELERATION
    -------------------------------------------------------------------------------------*/
    const [var_location, set_location] = useState([options[0]]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [expandedNodes, setExpandedNodes] = useState({});
    const [maxExpandedDepth, setMaxExpandedDepth] = useState(0);

    /* EVENT FUNCTIONS
    -------------------------------------------------------------------------------------*/
    const isOptionSelected = (option) => {
        return var_location.some((selectedNode) =>
            isNodeOrDescendant(selectedNode, option)
        );
    };

    const isNodeOrDescendant = (node, target) => {
        if (node.value === target.value || node.value === target.title)
            return true;
        if (node.children) {
            for (const child of node.children) {
                if (isNodeOrDescendant(child, target)) return true;
            }
        }
        return false;
    };

    const handleSelect = (node) => {
        set_location((prev) => {
            if (prev.length === 0) {
                return [node];
            } else {
                const currentParentID = prev[0].parentID;
                const newNodeParentID = node.parentID;

                if (currentParentID === newNodeParentID) {
                    const isSelected = prev.some(
                        (selected) => selected.value === node.value
                    );
                    if (isSelected) {
                        return prev.filter(
                            (selected) => selected.value !== node.value
                        );
                    } else {
                        return [...prev, node];
                    }
                } else {
                    return [node];
                }
            }
        });
    };

    // Helper to calculate max expanded depth
    function getMaxExpandedDepth(nodes, expanded, depth = 1) {
        let max = depth;
        for (const node of nodes) {
            const hasChildren =
                Array.isArray(node.children) && node.children.length > 0;
            if (hasChildren && expanded[node.value]) {
                const childMax = getMaxExpandedDepth(
                    node.children,
                    expanded,
                    depth + 1
                );
                if (childMax > max) max = childMax;
            }
        }
        return max;
    }

    const toggleNode = (id) => {
        setExpandedNodes((prev) => {
            const updated = {
                ...prev,
                [id]: !prev[id],
            };
            // Calculate new max depth after toggling
            setMaxExpandedDepth(getMaxExpandedDepth(options, updated));
            return updated;
        });
    };

    const renderMenuItems = (nodes, depth = 0) => {
        return nodes.map((node) => {
            const hasChildren =
                Array.isArray(node.children) && node.children.length > 0;
            const isExpanded = expandedNodes[node.value];

            return (
                <React.Fragment key={node.value || node.id}>
                    <MenuItem
                        style={{
                            paddingLeft: `${15 + depth * 12}px`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                        sx={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            width: 'auto', // up to 2x input width (assuming input ~250px)
                            minWidth: '250px',
                        }}
                        selected={isOptionSelected(node)}
                    >
                        <Checkbox
                            size="small"
                            checked={isOptionSelected(node)}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelect(node);
                            }}
                        />
                        <Box
                            sx={{ flexGrow: 1, cursor: 'pointer' }}
                            onClick={() => {
                                handleSelect(node);
                            }}
                        >
                            <ListItemText
                                sx={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}
                                primary={node.label || node.title}
                            />
                        </Box>

                        {hasChildren && (
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleNode(node.value || node.id);
                                }}
                                sx={{
                                    cursor: 'pointer',
                                    paddingLeft: 1,
                                    userSelect: 'none',
                                }}
                            >
                                <ExpandMoreOutlinedIcon
                                    sx={{
                                        cursor: 'pointer',
                                        transform: isExpanded
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                    }}
                                />
                            </Box>
                        )}
                    </MenuItem>

                    {hasChildren &&
                        isExpanded &&
                        renderMenuItems(node.children, depth + 1)}
                </React.Fragment>
            );
        });
    };

    function extractValuesAsString(array) {
        const values = new Set();
        const extract = (nodes) => {
            nodes.forEach((node) => {
                values.add(node.value || node.id);
                if (Array.isArray(node.children) && node.children.length > 0) {
                    extract(node.children);
                }
            });
        };
        extract(array);
        return Array.from(values).join(',');
    }

    /* EVENT LISTENERS
    -------------------------------------------------------------------------------------*/
    useEffect(() => {
        if (onChange) {
            onChange((prevValues) => ({
                ...prevValues,
                [name]: extractValuesAsString(var_location),
            }));
        }
    }, [var_location]);

    // Update maxExpandedDepth when options change (initial render)
    useEffect(() => {
        setMaxExpandedDepth(getMaxExpandedDepth(options, expandedNodes));
    }, [options]);

    return (
        <FormControl
            variant={variant}
            fullWidth
            {...(error && { error: true })}
            {...other}
        >
            <InputLabel>{label}</InputLabel>
            <Select
                open={dropdownOpen}
                onOpen={() => setDropdownOpen(true)}
                onClose={() => setDropdownOpen(false)}
                sx={{ backgroundColor: 'white' }}
                multiple
                name={name}
                disabled={disabled}
                value={var_location}
                input={<OutlinedInput label={label} />}
                renderValue={(selected) => {
                    if (!selected || selected.length === 0) return '';

                    const labels = selected.map(
                        (item) => item.label || item.title
                    );
                    const displayLabels =
                        labels[0]?.toLowerCase() === 'all'
                            ? 'All'
                            : 'Selected: ' + labels.length;
                    // const remainingCount = labels.length - 2;
                    return (
                        <Box>
                            <span>
                                {displayLabels}
                                {/* {remainingCount > 0
                                    ? ` +${remainingCount}`
                                    : ''} */}
                            </span>
                        </Box>
                    );
                }}
                MenuProps={{
                    variant: 'menu',
                    PaperProps: {
                        style: {
                            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                            width: 250 + (maxExpandedDepth - 1) * 60, // Increase width by 60px per expanded depth
                        },
                    },
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                }}
            >
                {options && renderMenuItems(options)}
            </Select>
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
}

export default MultiSelectGroupBy;
